import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, ShieldAlert } from 'lucide-react';
import './SessionTimeoutManager.css';

// Test mode configuration:
// To test, run in console: localStorage.setItem('testSessionTimeout', 'true')
// To reset to standard, run: localStorage.removeItem('testSessionTimeout')
const getDurations = () => {
    const isTest = localStorage.getItem('testSessionTimeout') === 'true';
    return {
        // sessionTimeout: isTest ? 19 * 1000 : 19 * 60 * 1000, // 19 seconds vs 19 minutes (original)
        sessionTimeout: isTest ? 19 * 1000 : 299 * 60 * 1000, // 5 hours total minus 1 minute warning (299 minutes)
        warningTimeout: isTest ? 10 * 1000 : 1 * 60 * 1000,  // 10 seconds vs 1 minute
        warningSecs: isTest ? 10 : 60
    };
};

const SessionTimeoutManager = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showModal, setShowModal] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(60);
    const btnRef = useRef(null);
    const terminateBtnRef = useRef(null);

    // Core interval to check session expiration
    useEffect(() => {
        const interval = setInterval(() => {
            const accessToken = localStorage.getItem('accessToken');

            // If user is not authenticated, clean up state and do nothing
            if (!accessToken) {
                if (showModal) {
                    setShowModal(false);
                }
                localStorage.removeItem('sessionExpiryTime');
                localStorage.removeItem('warningExpiryTime');
                return;
            }

            const { sessionTimeout, warningTimeout } = getDurations();
            const now = Date.now();

            // Initialize session expiry if not set
            let sessionExpiryStr = localStorage.getItem('sessionExpiryTime');
            if (!sessionExpiryStr) {
                const newExpiry = now + sessionTimeout;
                localStorage.setItem('sessionExpiryTime', newExpiry.toString());
                sessionExpiryStr = newExpiry.toString();
            }

            const sessionExpiry = parseInt(sessionExpiryStr, 10);
            let warningExpiryStr = localStorage.getItem('warningExpiryTime');
            let warningExpiry = warningExpiryStr ? parseInt(warningExpiryStr, 10) : null;

            // 1. Session elapsed (reached warning threshold)
            if (now >= sessionExpiry) {
                // If warning expiry isn't set yet, set it now
                if (!warningExpiry) {
                    warningExpiry = now + warningTimeout;
                    localStorage.setItem('warningExpiryTime', warningExpiry.toString());
                }

                // Check if warning time is up
                if (now >= warningExpiry) {
                    // SESSION EXPIRED - LOGOUT IMMEDIATELY
                    clearInterval(interval);

                    // Clear all auth items
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('user');
                    localStorage.removeItem('loginTimestamp');
                    localStorage.removeItem('dashboard');
                    localStorage.removeItem('rememberedEmail');
                    localStorage.removeItem('sessionExpiryTime');
                    localStorage.removeItem('warningExpiryTime');

                    setShowModal(false);
                    navigate('/signin');
                    return;
                }

                // Update countdown seconds left
                const remaining = Math.max(0, Math.ceil((warningExpiry - now) / 1000));
                setSecondsLeft(remaining);
                setShowModal(true);
            }
            // 2. Session still valid (e.g. extended in another tab)
            else {
                if (showModal) {
                    setShowModal(false);
                }
                if (warningExpiryStr) {
                    localStorage.removeItem('warningExpiryTime');
                }
            }

        }, 1000);

        return () => clearInterval(interval);
    }, [showModal, navigate, location.pathname]);

    // Handle session extension
    const handleContinueSession = () => {
        const { sessionTimeout } = getDurations();
        const newExpiry = Date.now() + sessionTimeout;
        localStorage.setItem('sessionExpiryTime', newExpiry.toString());
        localStorage.removeItem('warningExpiryTime');
        setShowModal(false);
    };

    // Handle session termination/logout manually
    const handleLogout = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('loginTimestamp');
        localStorage.removeItem('dashboard');
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('sessionExpiryTime');
        localStorage.removeItem('warningExpiryTime');

        setShowModal(false);
        navigate('/signin');
    };

    // Keyboard and Focus Trap when modal is visible
    useEffect(() => {
        if (showModal) {
            // Disable background scrolling
            document.body.style.overflow = 'hidden';

            const handleKeyDown = (e) => {
                // Prevent escape key from closing the modal
                if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                }
                // Force focus trap between the two buttons
                if (e.key === 'Tab') {
                    const focusables = [terminateBtnRef.current, btnRef.current].filter(Boolean);
                    if (focusables.length > 0) {
                        e.preventDefault();
                        e.stopPropagation();
                        const index = focusables.indexOf(document.activeElement);
                        if (e.shiftKey) {
                            const prevIndex = (index - 1 + focusables.length) % focusables.length;
                            focusables[prevIndex].focus();
                        } else {
                            const nextIndex = (index + 1) % focusables.length;
                            focusables[nextIndex].focus();
                        }
                    }
                }
            };

            window.addEventListener('keydown', handleKeyDown, true);

            // Auto-focus primary button on mount
            if (btnRef.current) {
                btnRef.current.focus();
            }

            return () => {
                document.body.style.overflow = 'unset';
                window.removeEventListener('keydown', handleKeyDown, true);
            };
        }
    }, [showModal]);

    // Don't render anything if modal is not active
    return (
        <AnimatePresence>
            {showModal && (
                <div className="session-timeout-overlay">
                    <motion.div
                        className="session-timeout-card"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 350 }}
                    >
                        {/* Glowing progress bar */}
                        <div className="session-timeout-progress-container">
                            <motion.div
                                className="session-timeout-progress-bar"
                                initial={{ width: '100%' }}
                                animate={{ width: `${(secondsLeft / getDurations().warningSecs) * 100}%` }}
                                transition={{ duration: 1, ease: 'linear' }}
                            />
                        </div>

                        <div className="session-timeout-content">
                            <div className="session-timeout-icon-wrap">
                                <ShieldAlert className="session-timeout-icon alert" size={32} />
                                <Clock className="session-timeout-icon clock-spin" size={20} />
                            </div>

                            <h3 className="session-timeout-title">Session Expiring</h3>

                            <p className="session-timeout-message">
                                Your session is going to expire in the next 1 minute. Do you want to continue the session?
                            </p>

                            <div className="session-timeout-countdown-badge">
                                <span>Time Remaining:</span>
                                <strong className="session-timeout-countdown-number">
                                    {secondsLeft}s
                                </strong>
                            </div>

                            <div className="session-timeout-actions">
                                <button
                                    ref={terminateBtnRef}
                                    className="session-timeout-btn secondary"
                                    onClick={handleLogout}
                                >
                                    Terminate
                                </button>
                                <button
                                    ref={btnRef}
                                    className="session-timeout-btn"
                                    onClick={handleContinueSession}
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default SessionTimeoutManager;
